import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Users, AlertTriangle, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const POLL_MS = 15000;
const PAGE_SIZE = 50;
const hhmmss = (d) => d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const ago = (s) => (s < 60 ? `há ${s} segundo${s === 1 ? "" : "s"}` : `há ${Math.floor(s / 60)} min`);

// Executa fn agora e a cada POLL_MS, apenas com a aba visivel.
function useVisiblePolling(fn, enabled = true) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    if (!enabled) return undefined;
    let timer = null;
    const sync = () => {
      clearInterval(timer); timer = null;
      if (document.visibilityState === "visible") { ref.current(); timer = setInterval(() => ref.current(), POLL_MS); }
    };
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => { clearInterval(timer); document.removeEventListener("visibilitychange", sync); };
  }, [enabled]);
}

export const PortalPresenceCard = () => {
  const [count, setCount] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/portal/presence", { params: { limit: 0 } });
      setCount(data.online_count); setUpdatedAt(new Date()); setFailed(false);
    } catch { setFailed(true); } // mantem o ultimo valor, marcado como desatualizado
  }, []);
  useVisiblePolling(load);

  return (
    <div className="kpi-card mb-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between" data-testid="portal-presence-card">
      <div className="flex items-center gap-5">
        <div className="relative flex h-12 w-12 items-center justify-center rounded-2xl bg-[#F3EAF7] text-[#6A1B9A]">
          <Users size={22} />
          <span className="absolute -right-1 -top-1 flex h-3.5 w-3.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
            <span className="relative inline-flex h-3.5 w-3.5 rounded-full border-2 border-white bg-emerald-500" data-testid="portal-presence-dot" />
          </span>
        </div>
        <div>
          <div className="kpi-label">Clientes online agora</div>
          <div className={`kpi-value tabular ${failed && count !== null ? "opacity-50" : ""}`} data-testid="portal-presence-count">
            {count === null ? "—" : count}
          </div>
          <div className="text-xs text-[#5C5A57]">Portal aberto e visível nos últimos 2 minutos</div>
        </div>
      </div>
      <div className="flex flex-col items-start gap-2 md:items-end">
        <button type="button" onClick={() => setOpen(true)} data-testid="portal-presence-open"
          className="rounded-full bg-[#6A1B9A] px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#4E1373]">
          Ver clientes online
        </button>
        {failed && (
          <div className="flex items-center gap-1.5 text-xs text-red-600" data-testid="portal-presence-error">
            <AlertTriangle size={13} /> Não foi possível atualizar{count !== null ? " · dado desatualizado" : ""}
          </div>
        )}
        <div className="text-[11px] text-[#5C5A57]" data-testid="portal-presence-updated">
          {updatedAt ? `Atualizado às ${hhmmss(updatedAt)}` : "Aguardando primeira atualização"}
        </div>
      </div>
      <PortalPresenceDialog open={open} onOpenChange={setOpen} />
    </div>
  );
};

function PortalPresenceDialog({ open, onOpenChange }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/portal/presence", { params: { page, limit: PAGE_SIZE } });
      setData(d); setFailed(false);
    } catch { setFailed(true); }
  }, [page]);
  useVisiblePolling(load, open);

  const total = data?.online_count ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="portal-presence-dialog">
        <DialogHeader>
          <DialogTitle>Clientes online agora</DialogTitle>
          <DialogDescription>
            Portal aberto e visível nos últimos 2 minutos. Indica presença recente, não interação contínua.
          </DialogDescription>
        </DialogHeader>
        {failed && (
          <div className="flex items-center gap-1.5 rounded-xl bg-red-50 px-3 py-2 text-xs text-red-700" data-testid="portal-presence-list-error">
            <AlertTriangle size={13} /> Não foi possível atualizar{data ? " · lista desatualizada" : ""}
          </div>
        )}
        <div className={`max-h-[60vh] overflow-y-auto ${failed && data ? "opacity-60" : ""}`}>
          {!data && !failed && <div className="py-8 text-center text-sm text-[#5C5A57]">Carregando…</div>}
          {data && data.items.length === 0 && (
            <div className="py-8 text-center text-sm text-[#5C5A57]" data-testid="portal-presence-empty">Nenhum cliente online no momento.</div>
          )}
          {data?.items.map((p) => <PresenceRow key={p.user_id} p={p} />)}
        </div>
        {data && pages > 1 && (
          <div className="flex items-center justify-end gap-3 text-xs text-[#5C5A57]">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="portal-presence-prev" className="disabled:opacity-40"><ChevronLeft size={16} /></button>
            <span>Página {page} de {pages}</span>
            <button type="button" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="portal-presence-next" className="disabled:opacity-40"><ChevronRight size={16} /></button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PresenceRow({ p }) {
  return (
    <div className="flex flex-col gap-2 border-b border-[#F0DEEA] py-3 last:border-0" data-testid={`portal-presence-row-${p.user_id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium text-[#2B0D2E]">{p.name}</div>
        <div className="flex items-center gap-3 text-xs">
          <span className="text-[#5C5A57]">{ago(p.seconds_ago)}</span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 font-semibold text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" /> Online
          </span>
        </div>
      </div>
      {p.cadastros.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {p.cadastros.map((c) => (
            <Link key={c.client_id} to={c.admin_path} data-testid={`portal-presence-cadastro-${c.client_id}`}
              className="rounded-full border border-[#E7D3EE] px-3 py-1 text-[11px] text-[#6A1B9A] transition-colors hover:bg-[#F3EAF7]">
              Cadastro · {c.reseller_name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
